import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CrudRowActions } from "@/components/shared/CrudRowActions";
import {
  useDepartments,
  useSetDepartmentActive,
} from "@/hooks/useDepartments";
import type { DepartmentListItem } from "@/services/departments";
import { useAuth } from "@/contexts/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";
import { PermissionButton } from "@/components/shared/PermissionButton";

function normalizeText(v: string) {
  return (v || "").toLowerCase().trim();
}

export default function DepartmentsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const userPermissions = user?.permissions ?? [];
  const canReadDepartments = userPermissions.includes(PERMISSIONS.DEPARTMENTS_READ);
  const canWriteDepartments = userPermissions.includes(PERMISSIONS.DEPARTMENTS_WRITE);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<DepartmentListItem | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const departmentsQ = useDepartments(true);
  const setActiveMut = useSetDepartmentActive();

  const items = departmentsQ.data ?? [];

  const filtered = useMemo(() => {
    const q = normalizeText(search);
    if (!q) return items;
    return items.filter((item) => normalizeText(item.name).includes(q));
  }, [items, search]);

  function openToggle(item: DepartmentListItem) {
    if (!canWriteDepartments) return;
    setSelected(item);
    setConfirmOpen(true);
  }

  async function handleConfirmToggle() {
    if (!selected || !canWriteDepartments) return;
    const nextActive = !selected.is_active;

    try {
      await setActiveMut.mutateAsync({
        id: selected.id,
        is_active: nextActive,
      });
      toast.success(nextActive ? "Departamento ativado" : "Departamento desativado");
      setConfirmOpen(false);
      setSelected(null);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao alterar status");
    }
  }

  return (
    <div>
      <PageHeader title="Departamentos" description="Cadastro dos departamentos da empresa">
        <PermissionButton
          permission={PERMISSIONS.DEPARTMENTS_WRITE}
          onClick={() => navigate("/cadastros/departamentos/novo")}
        >
          <Plus className="mr-2 h-4 w-4" />
          Novo Departamento
        </PermissionButton>
      </PageHeader>

      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome do departamento"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      {departmentsQ.isLoading ? (
        <Card className="p-6 text-sm text-muted-foreground">
          Carregando departamentos...
        </Card>
      ) : departmentsQ.isError ? (
        <Card className="p-6">
          <div className="text-sm text-destructive">
            {(departmentsQ.error as any)?.message || "Erro ao carregar departamentos"}
          </div>
        </Card>
      ) : filtered.length === 0 ? (
        <EmptyState title="Nenhum departamento encontrado" />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Departamento</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[140px] text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell>
                    <Badge variant={item.is_active ? "default" : "secondary"} className="text-xs">
                      {item.is_active ? "Ativo" : "Inativo"}
                    </Badge>
                  </TableCell>
                  
                  <TableCell className="text-right">
                    <CrudRowActions
                      onView={() => navigate(`/cadastros/departamentos/${item.id}`)}
                      onEdit={() => navigate(`/cadastros/departamentos/${item.id}/editar`)}
                      onToggleActive={() => openToggle(item)}
                      isActive={item.is_active}
                      disabled={setActiveMut.isPending}
                      viewPermission={PERMISSIONS.DEPARTMENTS_READ}
                      editPermission={PERMISSIONS.DEPARTMENTS_WRITE}
                      toggleActivePermission={PERMISSIONS.DEPARTMENTS_WRITE}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={selected?.is_active ? "Desativar Departamento" : "Ativar Departamento"}
        description={
          selected?.is_active
            ? "Deseja desativar este departamento?"
            : "Deseja ativar este departamento novamente?"
        }
        onConfirm={handleConfirmToggle}
        confirmLabel={selected?.is_active ? "Desativar" : "Ativar"}
        variant={selected?.is_active ? "destructive" : "default"}
      />
    </div>
  );
}
